import {
  fixedScaleHistoryPath,
  IndicatorContribution,
  IndicatorHistoryPoint,
  IndicatorName,
  OperationSystem,
} from "./indicator-history";
import { useAppLanguage } from "./i18n";

const INDICATORS: readonly IndicatorName[] = ["DA", "OP", "NA", "Ach", "VP", "5-HT"];
const OPERATIONS: readonly OperationSystem[] = ["steering", "throttle", "brake", "gear"];
const OPERATION_ABBREVIATION: Record<OperationSystem, string> = {
  steering: "STR",
  throttle: "THR",
  brake: "BRK",
  gear: "GEAR",
};

export interface IndicatorGaugeValue {
  value: number | null;
  causalId?: string;
  history: readonly IndicatorHistoryPoint[];
  contribution: IndicatorContribution | null;
}

export type IndicatorGaugeValues = Record<IndicatorName, IndicatorGaugeValue>;

export interface FiveHtGaugeDetail {
  reserve: number;
  daAttenuation: number;
  naFastAttenuation: number;
}

function clamped(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function IndicatorGauge({
  name,
  state,
  fiveHtDetail,
}: {
  name: IndicatorName;
  state: IndicatorGaugeValue;
  fiveHtDetail: FiveHtGaugeDetail | null;
}) {
  const { t, formatNumber } = useAppLanguage();
  const value = state.value === null || !Number.isFinite(state.value) ? null : clamped(state.value);
  const contribution = state.contribution;
  return (
    <div
      className={`gauge${name === "5-HT" ? " gauge-5ht" : ""}`}
      data-indicator={name}
      data-ready={value === null ? "false" : "true"}
      data-history-ready={state.history.length > 0 ? "true" : "false"}
      data-contribution-ready={contribution ? "true" : "false"}
      data-causal-id={state.causalId ?? ""}
    >
      <div className="gauge-copy"><span>{name}</span><strong>{value === null ? t("gauge.notReady") : formatNumber(value, { minimumFractionDigits: 3, maximumFractionDigits: 3 })}</strong></div>
      <div className="gauge-track"><i style={{ width: `${(value ?? 0) * 100}%` }} /></div>
      <div className="indicator-observation">
        <div className="indicator-history">
          <svg viewBox="0 0 112 26" preserveAspectRatio="none" role="img" aria-label={`${name}: ${t("gauge.recentSeries")}`}>
            <path className="indicator-history-baseline" d="M0,25.5 L112,25.5" />
            <path className="indicator-history-line" d={fixedScaleHistoryPath(state.history)} />
          </svg>
          <small>{state.history.length > 0 ? t("gauge.recentSeries") : t("gauge.seriesNotReady")}</small>
        </div>
        <div className="indicator-contribution" data-contribution-causal-id={contribution?.causalId ?? ""}>
          <span>{t("gauge.mainContribution")}</span><strong>{contribution
            ? contribution.hasDominantOperation && contribution.leadingOperation
              ? OPERATION_ABBREVIATION[contribution.leadingOperation]
              : t("gauge.noEffect")
            : t("gauge.notReady")}</strong>
          {contribution ? <div className="contribution-scores" aria-label={`${name}: ${t("gauge.mainContribution")}`}>
            {OPERATIONS.map(operation => (
              <small key={operation} data-operation={operation}>
                {OPERATION_ABBREVIATION[operation]} {contribution.scores[operation].toFixed(2)}
              </small>
            ))}
          </div> : <small>{t("gauge.noContribution")}</small>}
        </div>
      </div>
      {name === "5-HT" && <dl className="five-ht-detail">
        <div><dt>{t("gauge.reserve")}</dt><dd>{fiveHtDetail ? formatNumber(fiveHtDetail.reserve, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : t("gauge.notReady")}</dd></div>
        <div><dt>{t("gauge.daAttenuation")}</dt><dd>{fiveHtDetail ? formatNumber(fiveHtDetail.daAttenuation, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : t("gauge.notReady")}</dd></div>
        <div><dt>{t("gauge.naFastAttenuation")}</dt><dd>{fiveHtDetail ? formatNumber(fiveHtDetail.naFastAttenuation, { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : t("gauge.notReady")}</dd></div>
      </dl>}
    </div>
  );
}

export function IndicatorGaugeGroup({
  title,
  values,
  fiveHtDetail,
}: {
  title: string;
  values: IndicatorGaugeValues;
  fiveHtDetail: FiveHtGaugeDetail | null;
}) {
  const { t } = useAppLanguage();
  return (
    <section className="gauge-group" aria-label={`${title}: ${t("gauge.indicators")}`}>
      <div className="gauge-heading"><h3>{title}</h3><span>{t("gauge.indicators")}</span></div>
      <div className="gauges">
        {INDICATORS.map(name => (
          <IndicatorGauge key={name} name={name} state={values[name]} fiveHtDetail={fiveHtDetail} />
        ))}
      </div>
    </section>
  );
}
